import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { find, isNull, isUndefined, reject } from 'lodash-es'
import { Close, IosShare } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import {
  gridFilteredSortedRowIdsSelector,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useQuery } from '@tanstack/react-query'
import { ToggleButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { StyledDataGridBase } from '@ui/tables'
import { getHotDimensioningWallPositions } from '@queries'
import { buildErrorMessage, externalRessources } from 'src/constants'
import { baseColumns, timberRipColumns, unexportedFields } from './constants'

interface Props {
  ripType: 'timber' | 'standard'
  iconOnly?: boolean
  selected?: boolean
  onOpen?: () => void
  onClose?: () => void
}

const BlockpadDataButton = ({
  ripType,
  iconOnly = false,
  selected = false,
  onOpen,
  onClose,
}: Props): ReactElement => {
  const [open, setOpen] = useState(false)

  const handleClose = () => {
    setOpen(false)
    onClose?.()
  }
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const columns = useMemo(() => {
    if (ripType === 'standard') return baseColumns
    if (ripType === 'timber') return timberRipColumns

    return []
  }, [ripType])

  const {
    data = {
      timber_rips: [],
      standard_rips: [],
    },
    isFetching,
    refetch,
    error,
  } = useQuery<ExportHotDimensioningWallPositions | null>({
    queryKey: getHotDimensioningWallPositions.getKey(projectId),
    queryFn: async () => {
      return getHotDimensioningWallPositions.request(projectId)
    },
    enabled: false,
  })

  useEffect(() => {
    if (error)
      enqueueSnackbar(
        buildErrorMessage(
          error as Error | AxiosError,
          'Fehler beim Laden der Daten der Heißbemessung',
        ),
        {
          variant: 'error',
        },
      )
  }, [enqueueSnackbar, error])

  const handleOpen = () => {
    onOpen?.()
    refetch()
    setOpen(true)
  }

  const apiRef = useGridApiRef()

  const rowIdGetter = useCallback(
    (row: GridValidRowModel) => {
      if (ripType === 'timber') return row.rip_label
      if (ripType === 'standard') return row.wall_label
    },
    [ripType],
  )

  const handleCopyToClipboard = (includeHeader: boolean) => {
    if (isNull(data)) return

    const exportedColumns = reject(columns, col => unexportedFields.includes(col.field))

    const headers = exportedColumns.map(col => col.headerName).join('\t')

    const filteredRows = gridFilteredSortedRowIdsSelector(apiRef)

    const allRows = apiRef.current.getSortedRows()

    const exportedRows = filteredRows.map(
      rowId => find(allRows, row => rowIdGetter(row) === rowId) as GridValidRowModel,
    )

    const columnData = exportedRows
      .map(row =>
        exportedColumns
          .map(col => {
            const cellData = row[col.field as keyof ExportHotDimensioningTimberRip]

            if (isUndefined(cellData) || isNull(cellData)) return ''

            if (col.valueFormatter) return col.valueFormatter(cellData as never, row, col, apiRef)

            return cellData
          })
          .join('\t'),
      )
      .join('\n')

    const fullText = includeHeader ? `${headers}\n${columnData}` : columnData

    navigator.clipboard
      .writeText(fullText)
      .then(() => {
        enqueueSnackbar({
          message: 'Daten in Zwischenablage kopiert',
          variant: 'info',
        })
      })
      .catch(err => {
        enqueueSnackbar({
          message: `Wir konnten die Inhalte nicht in die Zwischenablage kopieren. Bitte versuche es manuell indem du die Zeilen der Tabelle selektierst. Fehler: ${err}`,
          variant: 'error',
        })
      })
  }

  const rows = useMemo(() => {
    if (ripType === 'timber') return data?.timber_rips
    if (ripType === 'standard') return data?.standard_rips
  }, [data?.standard_rips, data?.timber_rips, ripType])

  return (
    <>
      {iconOnly ? (
        <ToggleButton
          hint="Blockpad"
          tooltipPlacement="top"
          size="small"
          Icon={IosShare}
          selected={selected}
          onClick={handleOpen}
          data-cy="btn-toggle-blockpad"
        />
      ) : (
        <Button onClick={handleOpen} variant="outlined" size="small">
          Blockpad
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '.MuiPaper-root': {
            width: '100%',
          },
          '.MuiDialog-container': {
            maxWidth: '100%',
          },
        }}
        maxWidth={false}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} fontSize={15} fontWeight="normal">
          Einstellungen für Blockpad
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={theme => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <Close />
        </IconButton>

        <DialogContent dividers>
          <Stack direction="column" spacing={2} width="100%">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6">Heißbemessung </Typography>
              <Typography variant="caption">
                <a
                  href={externalRessources.hotDimensioningBlockpadLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  zur Vorlage
                </a>
              </Typography>
            </Stack>
            <StyledDataGridBase
              columns={columns}
              rows={isFetching || isUndefined(rows) ? [] : rows}
              density="compact"
              rowHeight={35}
              showCellVerticalBorder
              showColumnVerticalBorder
              hideFooter
              cellSelection
              disableRowSelectionOnClick
              headerFilters
              headerFilterHeight={75}
              loading={isFetching}
              sx={{ width: '100%', minHeight: 400 }}
              getRowId={rowIdGetter}
              apiRef={apiRef}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleCopyToClipboard(false)}
            disabled={isUndefined(rows) || rows.length === 0}
            variant="contained"
            size="small"
          >
            Ohne Kopfzeile Kopieren
          </Button>

          <Button
            onClick={() => handleCopyToClipboard(true)}
            disabled={isUndefined(rows) || rows.length === 0}
            variant="contained"
            size="small"
          >
            Mit Kopfzeile Kopieren
          </Button>
          <Button autoFocus onClick={handleClose} size="small">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default BlockpadDataButton
